import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Breadcrumb from "components/Common/Breadcrumb";
import {
	Form,
	FormGroup,
	Label,
	Input,
	Button,
	FormFeedback,
	Row,
	Col,
	Card,
	CardText,
	CardTitle,
	Table,
} from "reactstrap";
import relatoriosPowerBiService from "services/RelatoriosPowerBiService";
import Loading from "components/Common/Loading";
import contasPowerBiService from "services/ContasPowerBiService";
import userService from "services/UserService";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { FloppyDisk, Plus, Trash } from "@phosphor-icons/react";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";

const FormRelatoriosPowerBiConfig = () => {
	const { id } = useParams();
	const navigate = useNavigate();
	const [formData, setFormData] = useState({
		name: "",
		report_id: "",
		workspace_id: "",
		power_bi_config_id: "",
		users: [],
	});
	const [contasPowerBi, setContasPowerBi] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [dataLoading, setDataLoading] = useState(false);
	const [formErrors, setFormErrors] = useState({});
	const [companyUsers, setCompanyUsers] = useState();

	const fetchCompanyUsers = async () => {
		try {
			const users = await userService.getAllCompany();
			setCompanyUsers(users);
		} catch (error) {
			console.error("Erro ao buscar usuarios:", error);
		}
	};
	const fetchContas = async () => {
		try {
			const contas = await contasPowerBiService.getAll();
			setContasPowerBi(contas);
		} catch (error) {
			console.error("Erro ao buscar contas:", error);
		}
	};

	const fetchData = async () => {
		try {
			const data = await relatoriosPowerBiService.getById(id);
			setFormData({
				name: data.name || "",
				report_id: data.report_id || "",
				workspace_id: data.workspace_id || "",
				power_bi_config_id: data.power_bi_config_id || "",
				users: Array.isArray(data.users) ? data.users : [],
			});
		} catch (error) {
			console.error("Erro ao buscar dados do relatório:", error);
		}
	};

	const getAll = async () => {
		setDataLoading(true);
		try {
			await fetchCompanyUsers();
			await fetchContas();
			if (id) {
				await fetchData();
			}
		} catch (error) {
			console.error("Erro ao buscar dados:", error);
		} finally {
			setDataLoading(false);
		}
	};

	useEffect(() => {
		getAll();
	}, []);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData({
			...formData,
			[name]: value,
		});
	};

	const validate = () => {
		const errors = {};
		if (!formData.name) errors.name = "Campo obrigatório";
		if (!formData.report_id) errors.report_id = "Campo obrigatório";
		if (!formData.workspace_id) errors.workspace_id = "Campo obrigatório";
		if (!formData.power_bi_config_id)
			errors.power_bi_config_id = "Campo obrigatório";

		const userErrors = [];
		const addedUserIds = formData.users.map((user) => user.user_id);

		formData.users.forEach((user, index) => {
			if (!user.user_id) {
				userErrors[index] = "Selecione um usuário";
			} else if (addedUserIds.indexOf(user.user_id) !== index) {
				userErrors[index] = "Usuário já adicionado";
			}
		});

		if (userErrors.some((error) => error)) {
			errors.users = userErrors;
		}

		return errors;
	};

	const addUser = () => {
		setFormData({
			...formData,
			users: [...formData.users, { user_id: "", rls: "" }],
		});
	};

	const handleUserChange = (index, field, value) => {
		const updatedUsers = [...formData.users];
		updatedUsers[index] = {
			...updatedUsers[index],
			[field]: value,
		};
		setFormData({
			...formData,
			users: updatedUsers,
		});
	};

	const removeUser = (index) => {
		const updatedUsers = formData.users.filter((_, i) => i !== index);
		setFormData({
			...formData,
			users: updatedUsers,
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const errors = validate();
		if (Object.keys(errors).length > 0) {
			setFormErrors(errors);
		} else {
			setIsLoading(true);
			try {
				if (id) {
					await relatoriosPowerBiService.put(id, formData);
				} else {
					await relatoriosPowerBiService.post(formData);
				}

				setFormErrors({});
				await SendaCustomSwal.fire({
					title: "Sucesso!",
					text: id
						? "Relatório editado com sucesso."
						: "Relatório criado com sucesso.",
					iconHtml: '<i class="success-icon"></i>',
					confirmButtonText: "Ok",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});

				navigate("/relatorios-power-bi-config");
			} catch (error) {
				// Trate o erro conforme necessário
				setFormErrors(error.response?.data || {});
				await SendaCustomSwal.fire({
					title: "Erro",
					text: id
						? "Ocorreu um erro ao tentar editar o relatório."
						: "Ocorreu um erro ao tentar criar o relatório.",
					iconHtml: '<i class="error-icon"></i>',
					confirmButtonText: "Ok",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			} finally {
				setIsLoading(false);
			}
		}
	};

	return (
		<Loading isLoading={dataLoading}>
			<div className="page-content">
				<Breadcrumb
					title="Relatórios Power BI"
					breadcrumbItem="Cadastro de relatório"
					backLink="/relatorios-power-bi-config"
				/>

				<div
					className="container-fluid"
					style={{
						backgroundColor: "white",
						padding: 20,
						borderRadius: 10,
						marginTop: 25,
					}}
				>
					<Form onSubmit={handleSubmit}>
						<Row>
							<Col md={6}>
								<FormGroup>
									<Label for="name">
										Nome{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="text"
										name="name"
										id="name"
										value={formData.name}
										onChange={handleInputChange}
										invalid={!!formErrors.name}
										placeholder="Digite o nome"
									/>
									{formErrors.name && (
										<FormFeedback>
											{formErrors.name}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
							<Col md={6}>
								<FormGroup>
									<Label for="report_id">
										Report ID{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="text"
										name="report_id"
										id="report_id"
										value={formData.report_id}
										onChange={handleInputChange}
										invalid={!!formErrors.report_id}
										placeholder="Digite o Report ID"
									/>
									{formErrors.report_id && (
										<FormFeedback>
											{formErrors.report_id}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
						</Row>
						<Row>
							<Col md={6}>
								<FormGroup>
									<Label for="workspace_id">
										Workspace ID{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="text"
										name="workspace_id"
										id="workspace_id"
										value={formData.workspace_id}
										onChange={handleInputChange}
										invalid={!!formErrors.workspace_id}
										placeholder="Digite o Workspace ID"
									/>
									{formErrors.workspace_id && (
										<FormFeedback>
											{formErrors.workspace_id}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
							<Col md={6}>
								<FormGroup>
									<Label for="power_bi_config_id">
										Power BI Config{" "}
										<span style={{ color: "red" }}>*</span>
									</Label>
									<Input
										type="select"
										name="power_bi_config_id"
										id="power_bi_config_id"
										value={formData.power_bi_config_id}
										onChange={handleInputChange}
										invalid={
											!!formErrors.power_bi_config_id
										}
									>
										<option value="">
											Selecione uma configuração
										</option>
										{contasPowerBi?.map((config) => (
											<option
												key={config.id}
												value={config.id}
											>
												{config.name}
											</option>
										))}
									</Input>
									{formErrors.power_bi_config_id && (
										<FormFeedback>
											{formErrors.power_bi_config_id}
										</FormFeedback>
									)}
								</FormGroup>
							</Col>
						</Row>
						<Row>
							<Col md="24">
								<Card
									style={{
										display: "flex",
										alignContent: "center",
										justifyContent: "center",
										margin: "auto",
										border: "1px solid #e8e8e8",
										borderRadius: "10px",
										padding: "10px",
										marginTop: "10px",
									}}
								>
									<CardTitle className="d-flex justify-content-between align-items-center">
										<span>Regras de visualização RLS</span>
										<Button
											style={{
												background: "none",
												padding: 10,
												cursor: "pointer",
												color: "black",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: 2,
											}}
											onClick={addUser}
										>
											<Plus size={16} /> Adicionar Usuário
										</Button>
									</CardTitle>
									<CardText>
										<Table>
											<thead>
												<tr>
													<th>Nome do Usuário</th>
													<th>RLS</th>
													<th>Ações</th>
												</tr>
											</thead>
											<tbody>
												{Array.isArray(
													formData.users,
												) &&
													formData.users.map(
														(user, index) => (
															<tr key={index}>
																<td>
																	<Input
																		type="select"
																		value={
																			user.user_id
																		}
																		onChange={(
																			e,
																		) =>
																			handleUserChange(
																				index,
																				"user_id",
																				e
																					.target
																					.value,
																			)
																		}
																		invalid={
																			!!formErrors
																				.users?.[
																				index
																			]
																		}
																	>
																		<option value="">
																			Selecione
																			um
																			usuário
																		</option>
																		{companyUsers?.map(
																			(
																				userCompany,
																			) => (
																				<option
																					key={
																						userCompany.id
																					}
																					value={
																						userCompany.id
																					}
																				>
																					{
																						userCompany.name
																					}
																				</option>
																			),
																		)}
																		{/* Se o user_id não estiver em companyUsers, exiba o nome do usuário */}
																		{user.user_id &&
																			!companyUsers.some(
																				(
																					userCompany,
																				) =>
																					userCompany.id ===
																					user.user_id,
																			) && (
																				<option
																					value={
																						user.user_id
																					}
																					disabled
																				>
																					{formData.users.find(
																						(
																							u,
																						) =>
																							u.user_id ===
																							user.user_id,
																					)
																						?.user
																						?.name ||
																						"Usuário não encontrado"}
																				</option>
																			)}
																	</Input>
																	{formErrors
																		.users?.[
																		index
																	] && (
																		<FormFeedback>
																			{
																				formErrors
																					.users[
																					index
																				]
																			}
																		</FormFeedback>
																	)}
																</td>
																<td>
																	<Input
																		type="text"
																		value={
																			user.rls
																		}
																		onChange={(
																			e,
																		) =>
																			handleUserChange(
																				index,
																				"rls",
																				e
																					.target
																					.value,
																			)
																		}
																	/>
																</td>
																<td>
																	<Button
																		style={{
																			background:
																				"none",
																			cursor: "pointer",
																			color: "black",
																			display:
																				"flex",
																			alignItems:
																				"center",
																			justifyContent:
																				"center",
																			gap: 2,
																		}}
																		onClick={() =>
																			removeUser(
																				index,
																			)
																		}
																	>
																		<Trash
																			size={
																				16
																			}
																		/>{" "}
																		Remover
																	</Button>
																</td>
															</tr>
														),
													)}
											</tbody>
										</Table>
									</CardText>
								</Card>
							</Col>
						</Row>
						<Row>
							<div
								className="page-title-right"
								style={{
									display: "flex",
									justifyContent: "end",
								}}
							>
								<Button
									type="submit"
									style={{
										background: "none",
										padding: 10,
										cursor: "pointer",
										color: "black",
										marginTop: 10,
										width: 150,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: 2,
									}}
								>
									{isLoading ? (
										<span
											className="spinner-border spinner-border-sm mr-1"
											style={{ marginRight: 10 }}
										></span>
									) : null}
									<FloppyDisk size={16} /> Salvar
								</Button>
							</div>
						</Row>
					</Form>
				</div>
			</div>
		</Loading>
	);
};

export default FormRelatoriosPowerBiConfig;
